import React from 'react';
import { FiFileText, FiCheckCircle } from 'react-icons/fi';
import {
  FormSection,
  SectionTitle,
  FormGroup,
  Label,
  CheckboxGroup,
  CheckboxLabel,
  InfoBox,
  HelpText,
  CalculatedValue,
  CalculatedLabel,
  CalculatedAmount
} from '../common/FormStyles';
import { InformalProofs } from '@/types/application-forms';
import { UploadedDocument } from '../common/DocumentUpload';

interface InformalProofsSectionProps {
  data: Partial<InformalProofs>;
  onChange: (data: Partial<InformalProofs>) => void;
  readonly?: boolean;

  uploadedDocuments?: UploadedDocument[];
  onDocumentUpload?: (sectionKey: string, docType: string, file: File) => Promise<void>;
  onDocumentRemove?: (sectionKey: string, docType: string) => void;
}

// Mapping entre les types de documents uploadés et les labels de preuves
const DOCUMENT_TYPE_TO_PROOF_MAPPING: Record<string, string> = {
  'activity-photos': 'Photos lieu de travail',
  'client-testimonials': 'Attestations clients',
  'receipts-samples': 'Reçus/Factures achats',
  'cash-book': 'Cahier de comptes',
  'mobile-money-statements': 'Relevés Mobile Money',
  'community-letter': 'Attestation chef quartier',
  'witness-attestation': 'Attestation association',
  // Ajouter d'autres mappings si nécessaire
};

export const InformalProofsSection: React.FC<InformalProofsSectionProps> = ({
  data,
  onChange,
  readonly = false,
  uploadedDocuments = []
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleProofToggle = (proof: string, checked: boolean) => {
    const currentProofs = data.documents_disponibles || [];
    const newProofs = checked
      ? [...currentProofs, proof as any]
      : currentProofs.filter(p => p !== proof);
    handleChange('documents_disponibles', newProofs);
  };

  // Synchroniser automatiquement les cases cochées avec les documents uploadés
  React.useEffect(() => {
    const currentProofs = data.documents_disponibles || [];
    type ProofLabel =
      | "Photos lieu de travail"
      | "Attestations clients"
      | "Reçus/Factures achats"
      | "Cahier de comptes"
      | "Relevés Mobile Money"
      | "Attestation chef quartier"
      | "Attestation association"
      | "Carnet de commandes"
      | "Carte professionnelle"
      | "Échantillons produits"
      | "Autres documents";
    const proofsFromDocuments: ProofLabel[] = [];

    // Parcourir les documents uploadés et trouver les preuves correspondantes
    uploadedDocuments.forEach(doc => {
      const proofLabel = DOCUMENT_TYPE_TO_PROOF_MAPPING[doc.type] as ProofLabel | undefined;
      if (proofLabel && !currentProofs.includes(proofLabel)) {
        proofsFromDocuments.push(proofLabel);
      }
    });

    // Si de nouvelles preuves ont été trouvées, les ajouter
    if (proofsFromDocuments.length > 0) {
      const updatedProofs = [...currentProofs, ...proofsFromDocuments];
      handleChange('documents_disponibles', updatedProofs);
    }
  }, [uploadedDocuments]);

  const nombrePreuves = (data.documents_disponibles || []).length;
  const completudePercent = Math.min(100, (nombrePreuves / 6) * 100);

  return (
    <FormSection>
      <SectionTitle>
        <FiFileText />
        Preuves de votre Activité
      </SectionTitle>

      <InfoBox $type="info">
        <p>
          Plus vous fournissez de preuves de votre activité, plus votre dossier sera solide.
          Cochez tous les documents que vous pouvez fournir.
        </p>
      </InfoBox>

      <FormGroup>
        <Label>Documents que vous pouvez fournir</Label>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.documents_disponibles || []).includes('Photos lieu de travail')}
              onChange={e => handleProofToggle('Photos lieu de travail', e.target.checked)}
              disabled={readonly}
            />
            <strong>Photos de mon lieu de travail</strong>
            <HelpText style={{ marginLeft: '24px' }}>
              Photos montrant où vous travaillez (max 10 photos)
            </HelpText>
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.documents_disponibles || []).includes('Attestations clients')}
              onChange={e => handleProofToggle('Attestations clients', e.target.checked)}
              disabled={readonly}
            />
            <strong>Attestations de clients</strong>
            <HelpText style={{ marginLeft: '24px' }}>
              Lettres ou attestations de clients satisfaits (min 5 recommandé)
            </HelpText>
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.documents_disponibles || []).includes('Reçus/Factures achats')}
              onChange={e => handleProofToggle('Reçus/Factures achats', e.target.checked)}
              disabled={readonly}
            />
            <strong>Reçus / Factures d'achats</strong>
            <HelpText style={{ marginLeft: '24px' }}>
              Preuves d'achats de marchandises ou matériel
            </HelpText>
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.documents_disponibles || []).includes('Cahier de comptes')}
              onChange={e => handleProofToggle('Cahier de comptes', e.target.checked)}
              disabled={readonly}
            />
            <strong>Cahier de comptes / recettes</strong>
            <HelpText style={{ marginLeft: '24px' }}>
              Votre cahier où vous notez vos ventes et dépenses
            </HelpText>
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.documents_disponibles || []).includes('Relevés Mobile Money')}
              onChange={e => handleProofToggle('Relevés Mobile Money', e.target.checked)}
              disabled={readonly}
            />
            <strong>Relevés Mobile Money</strong>
            <HelpText style={{ marginLeft: '24px' }}>
              Historique de transactions sur 3 à 6 mois
            </HelpText>
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.documents_disponibles || []).includes('Attestation chef quartier')}
              onChange={e => handleProofToggle('Attestation chef quartier', e.target.checked)}
              disabled={readonly}
            />
            <strong>Attestation du chef de quartier</strong>
            <HelpText style={{ marginLeft: '24px' }}>
              Document confirmant votre résidence et activité
            </HelpText>
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.documents_disponibles || []).includes('Attestation association')}
              onChange={e => handleProofToggle('Attestation association', e.target.checked)}
              disabled={readonly}
            />
            <strong>Attestation d'association</strong>
            <HelpText style={{ marginLeft: '24px' }}>
              Si vous êtes membre d'une association professionnelle
            </HelpText>
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.documents_disponibles || []).includes('Carnet de commandes')}
              onChange={e => handleProofToggle('Carnet de commandes', e.target.checked)}
              disabled={readonly}
            />
            <strong>Carnet de commandes</strong>
            <HelpText style={{ marginLeft: '24px' }}>
              Photos de votre carnet de commandes clients
            </HelpText>
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.documents_disponibles || []).includes('Carte professionnelle')}
              onChange={e => handleProofToggle('Carte professionnelle', e.target.checked)}
              disabled={readonly}
            />
            <strong>Carte professionnelle</strong>
            <HelpText style={{ marginLeft: '24px' }}>
              Si vous avez une carte de votre métier
            </HelpText>
          </CheckboxLabel>
{/* 
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.documents_disponibles || []).includes('Vidéos activité')}
              onChange={e => handleProofToggle('Vidéos activité', e.target.checked)}
              disabled={readonly}
            />
            <strong>Vidéos de mon activité</strong>
            <HelpText style={{ marginLeft: '24px' }}>
              Courtes vidéos montrant votre travail (max 3, 30 secondes chacune)
            </HelpText>
          </CheckboxLabel> */}

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.documents_disponibles || []).includes('Échantillons produits')}
              onChange={e => handleProofToggle('Échantillons produits', e.target.checked)}
              disabled={readonly}
            />
            <strong>Photos d'échantillons de produits</strong>
            <HelpText style={{ marginLeft: '24px' }}>
              Si vous fabriquez des produits
            </HelpText>
          </CheckboxLabel>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.documents_disponibles || []).includes('Autres documents')}
              onChange={e => handleProofToggle('Autres documents', e.target.checked)}
              disabled={readonly}
            />
            <strong>Autres documents</strong>
            <HelpText style={{ marginLeft: '24px' }}>
              Tout autre document prouvant votre activité
            </HelpText>
          </CheckboxLabel>
        </CheckboxGroup>
      </FormGroup>

      <CalculatedValue>
        <CalculatedLabel>
          <FiCheckCircle style={{ marginRight: '8px' }} />
          Nombre de preuves fournies
        </CalculatedLabel>
        <CalculatedAmount>{nombrePreuves}</CalculatedAmount>
      </CalculatedValue>

      <FormGroup>
        <Label>Niveau de complétude du dossier</Label>
        <div
          style={{
            width: '100%',
            height: '30px',
            backgroundColor: '#e5e7eb',
            borderRadius: '15px',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <div
            style={{
              width: `${completudePercent}%`,
              height: '100%',
              backgroundColor:
                completudePercent < 30
                  ? '#ef4444'
                  : completudePercent < 60
                  ? '#f59e0b'
                  : '#10b981',
              transition: 'width 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            {Math.round(completudePercent)}%
          </div>
        </div>
        <HelpText>
          {completudePercent < 30 && '⚠️ Votre dossier a besoin de plus de preuves'}
          {completudePercent >= 30 && completudePercent < 60 && '⚡ Bon départ, continuez à ajouter des preuves'}
          {completudePercent >= 60 && completudePercent < 80 && '👍 Bon niveau de preuves'}
          {completudePercent >= 80 && '🌟 Excellent ! Dossier très complet'}
        </HelpText>
      </FormGroup>

      {nombrePreuves < 3 && (
        <InfoBox $type="warning">
          <p>
            <strong>Important :</strong> Vous n'avez coché que {nombrePreuves} preuve(s).
            Pour un dossier solide, essayez de fournir au moins 5 types de preuves différentes.
          </p>
        </InfoBox>
      )}

      {nombrePreuves >= 3 && nombrePreuves < 5 && (
        <InfoBox $type="info">
          <p>
            Bon début ! Vous avez {nombrePreuves} types de preuves. Essayez d'en ajouter
            quelques-unes de plus pour renforcer votre dossier.
          </p>
        </InfoBox>
      )}

      {nombrePreuves >= 5 && (
        <InfoBox $type="info">
          <p>
            <strong>Excellent !</strong> Vous avez {nombrePreuves} types de preuves.
            Votre dossier est bien documenté.
          </p>
        </InfoBox>
      )}

      <InfoBox $type="warning">
        <p>
          <strong>💡 Conseils pour améliorer votre dossier :</strong>
        </p>
        <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
          <li>Les photos de votre lieu de travail sont très importantes</li>
          <li>Les relevés Mobile Money sur 3-6 mois sont une excellente preuve de revenus</li>
          <li>Les attestations de clients montrent que vous travaillez régulièrement</li>
          <li>L'attestation du chef de quartier renforce votre crédibilité</li>
          <li>Plus vous avez de preuves variées, mieux c'est !</li>
        </ul>
      </InfoBox>

      <InfoBox $type="info">
        <p>
          <strong>Note :</strong> Vous pourrez télécharger tous ces documents après avoir
          soumis votre formulaire. Des instructions détaillées vous seront fournies.
        </p>
      </InfoBox>


      
    </FormSection>
  );
};
